import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import { BUDGET_DATA } from '../data/mockData';

export default function BudgetPlanner() {
  const chartOption = {
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(18, 18, 28, 0.9)',
      borderColor: 'rgba(138, 43, 226, 0.3)',
      textStyle: { color: '#E2E8F0' },
      formatter: '{b}: ${c} ({d}%)'
    },
    legend: {
      show: false
    },
    series: [
      {
        name: 'Budget Allocation',
        type: 'pie',
        radius: ['50%', '80%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 4,
          borderColor: '#050508',
          borderWidth: 2
        },
        label: {
          show: false,
        },
        color: ['#9D4EDD', '#E5C158', '#4A90E2', '#50E3C2', '#E2A9F3'],
        data: BUDGET_DATA.breakdown
      }
    ]
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="h-full flex flex-col pb-20 sm:pb-0 overflow-y-auto pr-2"
    >
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Financial Matrix</h1>
        <p className="text-[#94A3B8] text-sm mt-1">Resource allocation & tracking</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart Panel */}
        <div className="glass-panel p-6 flex flex-col items-center justify-center relative">
          <h2 className="text-lg font-semibold text-white w-full mb-2">Allocation Overview</h2>
          <div className="w-full h-[300px] relative">
            <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} />
            {/* Center Text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-sm text-[#94A3B8] uppercase tracking-wider">Total Limit</span>
              <span className="text-2xl font-bold text-white text-glow-purple">{formatCurrency(BUDGET_DATA.total)}</span>
            </div>
          </div>
        </div>

        {/* Details Panel */}
        <div className="glass-panel p-6 flex flex-col">
          <div className="flex justify-between items-end mb-6">
            <div>
              <h2 className="text-lg font-semibold text-white">Expenditure Status</h2>
              <p className="text-[#94A3B8] text-sm mt-1">Current utilization vs limits</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-mono text-[#E5C158]">{formatCurrency(BUDGET_DATA.spent)}</div>
              <div className="text-xs text-[#94A3B8] uppercase">Total Spent</div>
            </div>
          </div>

          <div className="space-y-5 flex-1 overflow-y-auto pr-2">
            {BUDGET_DATA.breakdown.map((item, idx) => {
              // Mock spent value for realism
              const spent = item.value * (0.5 + Math.random() * 0.4); 
              const percent = Math.min(100, (spent / item.value) * 100);
              const isOver = percent > 90;

              return (
                <div key={idx}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-white/90">{item.name}</span>
                    <span className="font-mono text-[#94A3B8]">{formatCurrency(spent)} / {formatCurrency(item.value)}</span>
                  </div>
                  <div className="h-2 bg-black/40 rounded-full overflow-hidden border border-white/5">
                    <div 
                      className={`h-full rounded-full transition-all duration-1000 ${isOver ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' : 'bg-gradient-to-r from-[#9D4EDD] to-[#E5C158]'}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
}